import React, { useState, useMemo, useEffect } from "react";
import "./Leaderboard.css";
import Pagination from "./Pagination";
import Table from "./Table";
import GoldMedal from "@/assets/svg/Schoolreports/gold-medal.svg";
import SilverMedal from "@/assets/svg/Schoolreports/silver-medal.svg";
import BronzeMedal from "@/assets/svg/Schoolreports/bronze-medal.svg";
import MWebLeaderboard from "./MWebLeaserboard";
import { motion, AnimatePresence } from "motion/react";
import { useReport } from "../../SchoolReportContext.tsx";

export default function Leaderboard({ students = [], notAttempted = [] }) {
  const tabNames = ["attempted", "not_attempted"];
  const [currentTab, setCurrentTab] = useState(tabNames[0]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 5,
  });
  const { eventMode } = useReport();

  useEffect(() => {
    if (students.length === 0 && notAttempted.length > 0) {
      setCurrentTab(tabNames[1]);
    } else {
      setCurrentTab(tabNames[0]);
    }
    // Reset to first page when data changes
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [students, notAttempted]);

  // Dynamic column configuration based on event mode
  const leaderboardColumns = useMemo(() => {
    const baseColumns = [
      { key: "rank", label: "RANK" },
      { key: "name", label: "STUDENT NAME" },
      { key: "marks", label: "MARKS OBTAINED" },
      { key: "accuracy", label: "ACCURACY" },
    ];

    // Only include time column for online events
    if (eventMode?.toLowerCase() === "online") {
      baseColumns.push({ key: "time", label: "TIME TAKEN" });
    }

    return baseColumns;
  }, [eventMode]);

  const formattedData = useMemo(() => {
    const rawData = currentTab === "attempted" ? students : notAttempted;

    return rawData.map((item) => {
      let rankDisplay;

      if (currentTab === "attempted") {
        const rank = parseInt(item.rank);
        if (rank === 1) {
          rankDisplay = (
            <img src={GoldMedal} alt="1st" className="medal-icon gold" />
          );
        } else if (rank === 2) {
          rankDisplay = (
            <img src={SilverMedal} alt="2nd" className="medal-icon silver" />
          );
        } else if (rank === 3) {
          rankDisplay = (
            <img src={BronzeMedal} alt="3rd" className="medal-icon bronze" />
          );
        } else {
          rankDisplay = rank;
        }
      } else {
        rankDisplay = <span className="not-attempted">Not Attempted</span>;
      }

      const baseData = {
        rank: rankDisplay,
        name: item.student_name || "--",
        marks:
          currentTab === "not_attempted" ? "--" : item.marks_obtained || "--",
        accuracy: currentTab === "not_attempted" ? "--" : item.accuracy || "--",
      };

      // Only include time data for online events
      if (eventMode?.toLowerCase() === "online") {
        baseData.time =
          currentTab === "not_attempted" ? "--" : item.total_time || "--";
      }

      return baseData;
    });
  }, [currentTab, students, notAttempted, eventMode]);

  const totalItems = formattedData.length;
  const indexOfLastItem = pagination.currentPage * pagination.itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - pagination.itemsPerPage;
  const currentItems = formattedData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  return (
    <div className="SCLeaderboard_mainCont">
      <div className="SCLeaderboard_CTA_cont">
        <div style={{ display: "flex", gap: "1rem" }}>
          {tabNames.map((tab) => (
            <motion.button
              key={tab}
              className={`SCLeaderboard_CTA ${
                currentTab === tab ? "SCLeaderboard_CTA_select" : ""
              }`}
              onClick={() => {
                setCurrentTab(tab);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.1 }}
            >
              {tab === "attempted" ? (
                <>
                  Attempted <span>({students.length})</span>
                </>
              ) : (
                <>
                  Unattempted <span>({notAttempted.length})</span>
                </>
              )}
            </motion.button>
          ))}
        </div>

        {formattedData.length > 0 && (
          <div className="SCLeaderboard_pagination_cont">
            <Pagination
              totalItems={totalItems}
              currentPage={pagination.currentPage}
              itemsPerPage={pagination.itemsPerPage}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        )}
      </div>

      {formattedData.length > 0 ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentTab}-${pagination.currentPage}-${eventMode}`} // Include eventMode in key to trigger animation on mode change
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="SCLeaderboard_detail_Cont"
          >
            <Table config={leaderboardColumns} data={currentItems} />
          </motion.div>
        </AnimatePresence>
      ) : (
        <div className="SCLeaderboard_noData">No data available.</div>
      )}

      <MWebLeaderboard
        data={currentTab === "attempted" ? students : notAttempted}
        isAttempted={currentTab === "attempted"}
        eventMode={eventMode}
      />
    </div>
  );
}
